/**
 * Google Analytics 4 - Event Tracking
 * 
 * Usage:
 *   import { trackEvent } from '@/lib/analytics'
 *   trackEvent('sign_up', { method: 'email' })
 */

import {
  safeGetItem,
  safeSetItem,
  safeSessionGetItem,
  safeSessionSetItem,
} from "@/lib/safeStorage";

// Extend window type for gtag
type GtagCommand = 'event' | 'config' | 'consent';

interface CustomWindow extends Window {
  gtag?: (
    command: GtagCommand,
    targetId: string,
    config?: Record<string, unknown>
  ) => void;
}

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

/**
 * Track a custom event in Google Analytics
 */
export function trackEvent(
  eventName: string,
  parameters?: Record<string, unknown>
) {
  if (typeof window === 'undefined') return;

  // 1. GA4 (legacy, kept for reporting continuity)
  const w = window as CustomWindow;
  if (GA_MEASUREMENT_ID && w.gtag) {
    w.gtag('event', eventName, parameters);
  }

  // 2. PostHog — single source for product analytics + funnels. Fails soft
  //    if the SDK isn't ready (no consent yet, key missing). All existing
  //    callers (trackSignUp, trackInterviewStart, trackCVUpload,
  //    trackUpgradeClick, trackFunnelStep, ...) flow through here.
  try {
    // Lazy require — posthog-js is already in the bundle via the provider.
    // We import dynamically to avoid SSR issues and to not crash if the
    // SDK hasn't been initialized (no consent / no API key).
    import("posthog-js").then((mod) => {
      try {
        mod.default.capture(eventName, parameters);
      } catch {
        /* SDK not initialized — skip silently */
      }
    });
  } catch {
    /* fail soft */
  }
}

/**
 * Track page view (automatic in GA4, but can be used for SPA navigation)
 */
export function trackPageView(url: string, title?: string) {
  if (typeof window === 'undefined' || !GA_MEASUREMENT_ID) {
    return;
  }
  
  const w = window as CustomWindow;
  if (!w.gtag) return;
  
  w.gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
  });
}

// ============================================
// Pre-defined events for Velyq
// ============================================

/** User signed up */
export function trackSignUp(method: 'email' | 'google' | 'microsoft') {
  trackEvent('sign_up', { method });
}

/** User logged in */
export function trackLogin(method: 'email' | 'google' | 'microsoft') {
  trackEvent('login', { method });
}

/** User started an interview */
export function trackInterviewStart(params: {
  interview_type: string;
  difficulty: string;
  question_count: number;
}) {
  trackEvent('interview_start', params);
}

/** User completed an interview */
export function trackInterviewComplete(params: {
  interview_type: string;
  score: number;
  duration_seconds: number;
}) {
  trackEvent('interview_complete', params);
}

/** User uploaded a CV */
export function trackCVUpload() {
  trackEvent('cv_upload');
}

/** User viewed premium page */
export function trackPremiumPageView() {
  trackEvent('view_premium');
}

/** User clicked upgrade CTA */
export function trackUpgradeClick(location: string) {
  trackEvent('upgrade_click', { location });
}

/** User submitted feedback */
export function trackFeedbackSubmit() {
  trackEvent('feedback_submit');
}

/** User clicked Discord link */
export function trackDiscordClick() {
  trackEvent('discord_click');
}

// ============================================
// UTM & Referrer Tracking (US-302)
// ============================================

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
}

/** Extract UTM params from URL and store them */
export function captureUTMParams(): UTMParams {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    referrer: document.referrer || undefined,
  };
  
  // Store in sessionStorage for attribution
  if (Object.values(utmParams).some(v => v)) {
    safeSessionSetItem('velyq_utm', JSON.stringify(utmParams));
  }

  return utmParams;
}

/** Get stored UTM params */
export function getStoredUTMParams(): UTMParams {
  if (typeof window === 'undefined') return {};
  const stored = safeSessionGetItem('velyq_utm');
  return stored ? JSON.parse(stored) : {};
}

/** Track with UTM attribution */
export function trackEventWithAttribution(
  eventName: string,
  parameters?: Record<string, unknown>
) {
  const utm = getStoredUTMParams();
  trackEvent(eventName, { ...parameters, ...utm });
}

// ============================================
// Funnel Tracking (US-307)
// ============================================

export type FunnelStep = 
  | 'landing_view'
  | 'register_start'
  | 'register_complete'
  | 'email_verified'
  | 'onboarding_start'
  | 'onboarding_profile'
  | 'onboarding_cv'
  | 'onboarding_complete'
  | 'first_interview_start'
  | 'first_interview_complete';

/** Track funnel progression */
export function trackFunnelStep(step: FunnelStep, metadata?: Record<string, unknown>) {
  trackEventWithAttribution(`funnel_${step}`, {
    step,
    timestamp: new Date().toISOString(),
    ...metadata,
  });
  
  // Store funnel progress locally
  if (typeof window !== 'undefined') {
    const funnel = JSON.parse(safeGetItem('velyq_funnel') || '[]');
    funnel.push({ step, timestamp: Date.now() });
    safeSetItem('velyq_funnel', JSON.stringify(funnel));
  }
}

// ============================================
// CTA Click Tracking (US-301)
// ============================================

export type CTALocation = 
  | 'hero'
  | 'pricing'
  | 'navbar'
  | 'footer'
  | 'interview_card'
  | 'premium_modal'
  | 'landing_demo'
  | 'landing_features'
  | 'landing_testimonials'
  | 'landing_cta_bottom';

/** Track CTA button clicks */
export function trackCTAClick(
  ctaType: 'register' | 'login' | 'start_interview' | 'upgrade' | 'pricing',
  location: CTALocation
) {
  trackEvent('cta_click', {
    cta_type: ctaType,
    location,
  });
}

// ============================================
// Conversion Tracking (US-306)
// ============================================

/** Track conversion events with value */
export function trackConversion(
  conversionType: 'registration' | 'first_interview' | 'premium_subscribe' | 'cv_upload',
  value?: number
) {
  const utm = getStoredUTMParams();
  trackEvent('conversion', {
    conversion_type: conversionType,
    value,
    currency: 'EUR',
    ...utm,
  });
}

// ============================================
// Retention Tracking (US-305)
// ============================================

const LAST_VISIT_KEY = 'velyq_last_visit';
const VISIT_COUNT_KEY = 'velyq_visit_count';

/** Track user return/retention */
export function trackUserReturn() {
  if (typeof window === 'undefined') return;
  
  const now = Date.now();
  const lastVisit = safeGetItem(LAST_VISIT_KEY);
  const visitCount = parseInt(safeGetItem(VISIT_COUNT_KEY) || '0', 10) + 1;

  // Update visit count
  safeSetItem(VISIT_COUNT_KEY, visitCount.toString());
  safeSetItem(LAST_VISIT_KEY, now.toString());
  
  if (lastVisit) {
    const daysSinceLastVisit = Math.floor((now - parseInt(lastVisit, 10)) / (1000 * 60 * 60 * 24));
    
    trackEvent('user_return', {
      days_since_last_visit: daysSinceLastVisit,
      total_visits: visitCount,
      is_returning: daysSinceLastVisit > 0,
    });
  } else {
    // First visit
    trackEvent('first_visit', {
      total_visits: 1,
    });
  }
}

/** Get user retention stats */
export function getRetentionStats() {
  if (typeof window === 'undefined') return null;
  
  const lastVisit = safeGetItem(LAST_VISIT_KEY);
  const visitCount = parseInt(safeGetItem(VISIT_COUNT_KEY) || '0', 10);

  return {
    lastVisit: lastVisit ? new Date(parseInt(lastVisit, 10)) : null,
    visitCount,
  };
}
